import {ExternalStore} from "./ExternalStore";
import type {PromiseState} from "./PromiseWithState";

type RequestState<T> = {state: "idle"} | PromiseState<T>;

/**
 * The state of the latest `call`. A call while one is pending returns that pending promise. The
 * store sends the request, not a React state update, so a call still goes out after the component
 * using it unmounted.
 */
export class RequestStore<Args extends unknown[], T> extends ExternalStore<RequestState<T>> {
  private snapshot: RequestState<T> = {state: "idle"};

  constructor(private request: (...args: Args) => Promise<T>) {
    super();
  }

  getSnapshot = () => this.snapshot;

  call = (...args: Args): Promise<T> => {
    if (this.snapshot.state === "pending") return this.snapshot.promise;
    const promise = this.request(...args);
    this.set({state: "pending", promise});
    promise.then(
      (value) => this.set({state: "fulfilled", value}),
      (error) => this.set({state: "rejected", error})
    );
    return promise;
  };

  private set(next: RequestState<T>) {
    this.snapshot = next;
    this.notify();
  }
}
